import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import PathHints from "csi/core/PathHints";
import Heading from "csi/def/Heading";
import locale from "csi/locale";
import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import ReflectionClass from "csi/reflection/ReflectionClass";
import types from "csi/types";
import { C8AttributeKey, C8AttributeType, C8DateRangeArray, C8PrimitiveValue, VALUE_TYPE, attributeValueAsArray, isRefValueType } from "csi/typings";
import { isDefined, isNullOrUndefined, stringBoolean2Boolean } from "csi/utility/coreUtility";
import { FORMAT_DATETIME, FORMAT_DATETIME_SECONDS, FORMAT_DIFF, FORMAT_TIME, areDateRangeValuesEqual, convertDateRangeToUTC, dateRangeSelectionIsInTheFuture, dateRangeSelectionIsNotCustom, getTimeRange, isDateRangeBlanks, isDateRangeNonBlanks, isDateRangeValue, updateDateRange } from "csi/utility/dateUtility";
import { elementAddCss } from "csi/utility/htmlUtility";
import { getOpForMultiPath, getReverseOpToNegate } from "csi/utility/operatorUtility";
import { PATH_OP_INDEX, pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import ExtractQuery from "csi/xml/ExtractQuery";
import Query from "csi/xml/Query";
import Memory from "dojo/store/Memory";
import { isEqual, last, pick, transform } from "lodash";
import { Renderer } from "../heading/Renderer";
import { convertToStandardPath, parsePathSteps } from "./extendedPathService";

/**
 * Additional parameters that can affect filtering
 */
export interface SpecFilterParams {
	locale?: string;
}

/**
 * Meta-data about filter operator
 */
export interface OperatorStoreItem {
	id: string;
	name: string;
	noValue?: boolean;
}

/**
 * Parameters to apply to DOMNode displaying label for Spec
 */
export interface SpecHTMLElementParams {
	className?: string;
	colSpan?: number;
	dataset?: Record<string, string | number | boolean>;
	innerHTML?: string;
	innerText?: string;
	multiLineText?: string;
}

/**
 * QuerySpec defines the necessary information to query/filter for a given node parameter or attribute
 */
export interface QuerySpec {
	/** allows filter to select existing value and enter specific values, true if attribute is string type with other formats, or Boolean, Ref, or Enum type */
	/** show different widgets (single select, multi select, text field) in string and ref attributes (for string with different format) instead of only text field (string attributes with no format) */
	allowFilteringBySelectionOfExistingValues?: boolean;

	/** true if query does not support returning individual values - only blanks vs non - blanks */
	blanksOnly?: boolean;

	/** blanksOperator - one of the following based on attribute type(E -enum, L - localized, S - string, T - time) */
	blanksOperator?: string;

	/** id - node attribute id */
	id?: string;

	/** index/key for container attribute */
	index?: string;

	/** true if attribute value cannot be filtered */
	notFilterable?: boolean;

	/** true if attribute value cannot be sorted */
	notSortable?: boolean;

	/** true if query does not support querying by blanks (or non-blanks) */
	omitBlanks?: boolean;

	/** true if query does not support returning correct counts for blanks vs non-blanks */
	omitBlankCounts?: boolean;

	/** list of available filter operators if available */
	ops?: string[];

	/** path to parameter/attribute */
	path?: string;

	/** path to parameter/attribute relative to its parent matrix (if sub-spec) */
	pathInMatrix?: string;

	/** node parameter */
	prop?: string;

	/** available to be selected as relative filter value */
	relative?: boolean;

	/** renderer used for specifying query filter */
	ren?: Renderer;

	/** value type for filtering/sorting - can be different than spec type (i.e. ref -> string) */
	type?: string;

}

const HEADER_CELL_PARAMS = ["className", "colSpan"];
const HEADER_CONTENT_PARAMS = ["innerHTML", "innerText", "multiLineText", "style"];
const RE_PARENT_PATH = /:__Parent__(\([^)]+\))?$/;

const {
	COLUMN_WIDTHS,
	SMALL,
	MEDIUM,
	LARGE,
} = types;

export default class Spec {
	/** set to true to indicate that value can be drag-filled to other columns in matrix with same heading */
	allowCopyToMatrixColumns = false;

	/** index of columnUrl in matrix */
	columnIndex?: number;

	/** node url for matrix specs working with dynamic node columns */
	columnUrl?: string;

	/** extra css for column, apart from heading CSS */
	css?: string;

	/** path to use with WBS ElementsByName dynamic matrix */
	customMatrixStandardPath?: string;

	/** format used to render heading */
	// FIX: this is only used to support SiteAdmin/RevisionHeadingsController - ideally this would not be necessary
	extraFilter?: Query;

	/** set to true if spec is the first in a matrix */
	firstInMatrix = false;

	/** format used to render heading */
	format?: string;

	/** heading used to render column */
	heading: Heading;

	/** object mixed in with domNode representing spec label or header, including parameters such as innerText, innerHTML, colSpan, class, style, title */
	headerParams?: SpecHTMLElementParams;

	/** container index to add to context */
	index?: number | string;

	/** unique identifier for Spec */
	key: Key;

	/** if spec for a matrix, then contains the extended path to nodes in the matrix for this column */
	matrixExtendedPath?: ExtendedPath;

	/** name of spec */
	name?: string;

	/** custom parameters for special cases */
	params?: Record<string, any>;

	/** reference to parent spec if sub under matrix spec */
	parentSpec?: Spec;

	/** auto-generated unique identifier based on data to query for independent of level with following pattern - ($prop|id)(/index)(:path) */
	// FIX: can/should this be merged with "key", or at least by-product of it?
	queryKey?: string;

	/** properties necessary to translate spec into query for filtering or sorting */
	querySpec?: QuerySpec;

	/** reflection attribute if in model */
	ra?: ReflectionAttribute;

	/** reflection class owning attribute if in model */
	rc?: ReflectionClass;

	/** if attribute is a relation, then indicate target class */
	rcDest?: ReflectionClass;

	/** properties necessary to translate spec into query for sorting if different from filtering */
	sortQuerySpec?: QuerySpec;

	/** type of attribute / property */
	type?: C8AttributeType;

	// The following properties do not apply to sub-columns...

	/** unique identifier for spec within Specs */
	sid?: string;

	/** sub-columns for existing column */
	subs?: Spec[];

	/** object mixed in with domNode representing spec value, including parameters such as colSpan, class, style */
	valueParams?: SpecHTMLElementParams;

	constructor(params: Partial<Spec>) {
		Object.assign(this, params);

		const { name, subs } = this;
		const h = this.heading;

		if (h && !this.key) {
			const { sourceKey } = h;
			const extendedPath = sourceKey?.extendedPath;

			this.format = h.m_format;
			this.index ??= h.m_index;
			this.ra = h._rca;
			this.type = h.getAttrType();
			this.rc = extendedPath?.endClass || this.ra?.getOwner();

			if (this.ra && xml.attributeTypeIsRef(this.type)) {
				this.rcDest = this.ra.getDestinationClass(this.rc);
			}

			this.key = new Key({
				extendedPath,
				id: h.m_id,
				index: this.index,
				matrix: h.parentMatrixGroupId,
				name: h.m_nameOrig,
				path: h.m_path,
				prop: h.m_prop,
			});
		}

		let { headerParams } = this;
		if (!headerParams) {
			headerParams = this.headerParams = {};

			const text = h?.getLabel() || name;
			if (text) {
				headerParams.multiLineText = text;
			}

			// FIX?: this is table-specific
			if (subs) {
				headerParams.colSpan = subs.length;
			}
		}

		// set name for spec if not already set
		this.name ||= this.getText()?.replace(/\n/g, " ") ?? "";

		this.setQuerySpec(this.querySpec);

		// assign parent to all sub specs
		subs?.forEach((sc) => {
			sc.parentSpec = this;
		});

		// process matrix specs and initialize pathInMatrix for querySpec
		if (this.isMatrixGroup && subs) {
			const { matrixGroupStandardPath } = this;
			const n = matrixGroupStandardPath ? parsePathSteps(matrixGroupStandardPath).length : 0;
			let epMatrix: ExtendedPath;

			subs?.forEach((sc) => {
				const { querySpec } = sc;

				if (querySpec?.path) {
					const epSpec = sc.getExtendedPath();
					const pathHints = new PathHints(epSpec.pathHints);
					pathHints.add(epSpec);
					const ep = new ExtendedPath(epSpec.getStartType(), pathHints);

					try {
						ep.addPath(querySpec.path);

						if (epMatrix) {
							ep.shiftSteps(n, true);
						} else {
							epMatrix = this.matrixExtendedPath = ep.shiftSteps(n);
						}

						querySpec.pathInMatrix = ep.getQueryPath();
					} catch (ex) {
						// extended path could not be resolved, so restrict query
						sc.queryKey = null;
						sc.querySpec = null;
					}
				}
			});
		}
	}

	/** key for Spec which is based on identifying properties of Heading */
	get headingKey(): string {
		return this.heading.getKey();
	}

	/** key used in filterSettings to hold column matrix filters */
	get matrixFilterKey(): C8AttributeKey | undefined {
		const { isMatrix, key } = this;
		if (isMatrix) {
			return Key.getMatrixConfigurableFilterKey(key.components);
		}
	}

	/** matrix group id */
	get matrixGroupId(): string | undefined {
		return this.heading?.matrixGroupId;
	}

	/** matrix path in standard form (i.e. "Child:ProductColors"), but only if this is a matrix group */
	get matrixGroupStandardPath(): string | undefined {
		const { isMatrixGroup, matrixStandardPath } = this;
		return isMatrixGroup ? matrixStandardPath : undefined;
	}

	/** matrix path in standard form (i.e. "Child:ProductColors") */
	get matrixStandardPath(): string | undefined {
		// TODO: need to figure out if this (should) ever exists when not a matrix group?
		const { heading, isMatrix } = this;
		return isMatrix ? heading.matrixGroupPath : undefined;
	}

	/** target class of matrix */
	get matrixTargetClass(): ReflectionClass | undefined {
		const { isMatrix, matrixExtendedPath, rc, rcDest } = this;

		if (matrixExtendedPath) {
			return matrixExtendedPath.endClass;
		}

		// TODO: in future, matrixExtendedPath should always be defined (for matrix groups) and won't need this here
		return isMatrix ? (rcDest || rc) : undefined;
	}

	/** parent spec if there is one, otherwise this */
	get rootSpec(): Spec {
		return this.parentSpec || this;
	}

	/** return configured paths on any sub headings */
	get configuredSubHeadingPaths(): Set<string> {
		return transform(this.heading.configuredSubHeadings, (result, { m_path }) => {
			if (m_path) {
				result.add(m_path);
			}
		}, new Set<string>());
	}

	/** unique id for Spec */
	get uniqueId(): C8AttributeKey {
		return this.key?.toString();
	}

	/** supports time format */
	get supportsTimeFormat(): boolean {
		const { type, format } = this;
		return type === xml.ATTR_TIME &&
			format === FORMAT_TIME ||
			format === FORMAT_DATETIME ||
			format === FORMAT_DATETIME_SECONDS ||
			format === FORMAT_DIFF;
	}

	/**
	 * Add ad-hoc filters to spec's query
	 */
	applyAdHocFilter(q: Query, value: C8PrimitiveValue | C8PrimitiveValue[], bInMatrix?: boolean): void {
		value = [].concat(value);

		if (!value.length) {
			return;
		}

		const filterValues = value as C8PrimitiveValue[];
		const dateRange = value as C8DateRangeArray;

		if (this.isDateAttribute() && isDateRangeValue(dateRange)) {
			// handle date ranges
			let op = xml.OP_RANGE;

			if (isDateRangeBlanks(dateRange)) {
				// use special operators for blank values
				op = this.getBlankOperator(false);
				value = null;
			} else if (isDateRangeNonBlanks(dateRange)) {
				// use special operators for non-blank values
				op = this.getBlankOperator(true);
				value = null;
			}

			this.applyFilter(q, op, value, bInMatrix);
		} else {
			const bMultiple = (filterValues.length > 1);
			const qn = bMultiple ? q.Or() : q;

			filterValues.forEach((v: C8PrimitiveValue) => {
				// default operator for ad-hoc filters is equals
				const stringValue = v as string;
				let op = xml.OP_EQUALS;

				if ((v === "") || (v === xml.VALUE_BLANK) || (v === xml.VALUE_NONBLANK)) {
					// use special operators for blank/non-blank values
					op = this.getBlankOperator(v === xml.VALUE_NONBLANK);
					v = this.querySpec.ren?.blankValue || null;
				} else if (xml.queryIsMatchValue(stringValue)) {
					// if new value, then use match operator
					op = xml.OP_MATCHES;
					v = xml.queryToServerMatchValue(stringValue);
				}

				this.applyFilter(qn, op, v, bInMatrix, bMultiple);
			});

			if (bMultiple && this.extraFilter) {
				qn.importQuery(this.extraFilter);
			}
		}
	}

	/**
	 * csiHeading.toHtml will override CSS from spec, so reapply here
	 */
	applyCss(td: HTMLElement): void {
		elementAddCss(td, this.valueParams?.className);
	}

	/**
	 * Add filters from queryObject that belong to matrix to given query q
	 */
	applyMatrixFilters(q: Query, queryObject: Record<string, C8PrimitiveValue | C8PrimitiveValue[]>): Query {
		if (this.isMatrix) {
			this.subs?.forEach((sc) => {
				const v = queryObject[sc.queryKey];
				if (v != null) {
					sc.applyAdHocFilter(q, v, true);
				}
			});
		}

		return q;
	}

	/**
	 * Apply filter to given query and return it
	 */
	applyFilter(
		qn: Query,
		op: string,
		value: C8PrimitiveValue | C8PrimitiveValue[],
		bInMatrix?: boolean,
		bMultiple?: boolean,
		filterParams?: SpecFilterParams,
	): Query {
		// handle slicer extract query filtering separately
		if (qn instanceof ExtractQuery) {
			this._applyExtractFilter(qn as ExtractQuery, op, attributeValueAsArray(value));
			return qn;
		}

		const { extraFilter, format, querySpec } = this;
		if (!querySpec) {
			console.warn("No query spec for " + this.uniqueId);
			return qn;
		}
		const { index, path, pathInMatrix } = querySpec;
		const loc = filterParams?.locale;
		const ep = this.getExtendedPath();
		const filterPath = bInMatrix ? pathInMatrix : path;
		const filterValues = value as C8PrimitiveValue[];

		// replace query spec index (from current view locale) with that stored in custom view
		const filterIndex = (index && loc && this.isLocalizedAttribute()) ? loc : index;

		const isDateAttribute = this.isDateAttribute();

		const dateRange = value as C8DateRangeArray;

		// cover the case where the attribute is 0...many relationship, treat it as the same as container
		if (ep && !ep.isSingle) {
			op = getOpForMultiPath(op);
		}

		// apply extra filter for Spec if not applying same filter for multiple values (ad-hoc case)
		if (!bMultiple && extraFilter) {
			qn.importQuery(extraFilter);
		}

		if (!Array.isArray(value)) {
			if (isDateAttribute && value) {
				value = Number(value);

				// if time attribute, then find time value that is appropriate for given operator based on format range (day/minute/second)
				if (op != xml.OP_EQUALS) {
					const range = getTimeRange(format, new Date(value));

					if (op === xml.OP_RANGE) {
						// test for value in given range
						this._applyDateRangeFilter(qn, querySpec, filterPath, op, range, filterIndex, format);
						return;
					}

					// determine filter value from range based on operator
					value = range[((op == xml.OP_GREATER_EQUAL) || (op == xml.OP_LESS_THAN)) ? 0 : 1];
				} else {
					// the only time we can get here is when the value is relative AND the attribute is Time related,
					// and we currently treat OP_EQUALS as a special case where it applies a one day range
					const range = getTimeRange(null, new Date(value));
					this._applyDateRangeFilter(qn, querySpec, filterPath, op, range, filterIndex, format);
					return;
				}
			}

			// test for single value
			this._applyFilterValue(qn, querySpec, filterPath, op, value, filterIndex);
		} else if (isDateAttribute) {
			this._applyDateRangeFilter(qn, querySpec, filterPath, op, dateRange, filterIndex, format);
		} else if (filterValues.length) {
			// check for negative operators, and if so, reverse them under a NOT condition
			const reverseOp = getReverseOpToNegate(op);

			if (reverseOp) {
				qn = qn.Not();
				op = reverseOp;
			}

			// test for one of the given values
			qn = op === xml.OP_NOT_EQUALS ? qn.And() : qn.Or();

			filterValues.forEach((v) => {
				this._applyFilterValue(qn, querySpec, filterPath, op, v, filterIndex);
			});
		}

		return qn;
	}

	/**
	 * Apply filter by a date range
	 */
	_applyDateRangeFilter(
		qn: Query,
		querySpec: QuerySpec,
		filterPath: string,
		op: string,
		dateRange: C8DateRangeArray,
		filterIndex?: number | string,
		format?: string,
	): void {
		const opIsBlank = op.includes(xml.OP_BLANKS) || op === xml.OP_NULL;
		const opIsNonBlank = op.includes(xml.OP_NON_BLANKS) || op === xml.OP_NOT_NULL;
		if (opIsBlank || opIsNonBlank) {
			op = this.getBlankOperator(opIsNonBlank);
			this._applyFilterValue(qn, querySpec, filterPath, op, null, filterIndex);
		}

		if (!dateRange?.length) {
			return;
		}

		const rangeAnd = qn.And();
		const updateDateRangeAccordingToSelection = dateRangeSelectionIsNotCustom(dateRange);
		if (op === xml.OP_RANGE ||
			op === xml.OP_WITHIN ||
			(op === xml.OP_EQUALS &&
				(!this.supportsTimeFormat || updateDateRangeAccordingToSelection)
			)
		) {
			// get updated values for relative data ranges, such as today, past week, and past month
			dateRange = updateDateRange(dateRange, op === xml.OP_EQUALS);

			if (format === types.UNIVERSAL_DATE) {
				dateRange = convertDateRangeToUTC(dateRange);
			}

			// test for value in given range
			this._applyFilterValue(rangeAnd, querySpec, filterPath, xml.OP_GREATER_EQUAL, dateRange[0]);
			this._applyFilterValue(rangeAnd, querySpec, filterPath, xml.OP_LESS_EQUAL, dateRange[1]);
		} else {
			if (updateDateRangeAccordingToSelection) {
				dateRange = updateDateRange(dateRange, false, true);
			}
			const isInTheFuture = dateRangeSelectionIsInTheFuture(dateRange);
			this._applyFilterValue(qn, querySpec, filterPath, op, isInTheFuture ? dateRange[1] : dateRange[0], filterIndex);
		}
	}

	/**
	 * Extract stores (for Slicer) support a much more limited query language, so handle separately here
	 */
	_applyExtractFilter(qn: ExtractQuery, op: string, value: C8PrimitiveValue[]): void {
		attributeValueAsArray(value).forEach((v) => {
			qn.Filter(this.queryKey, op, v);
		});
	}

	/**
	 * Determine filters to apply based on specified operator and values and context
	 */
	_applyFilterValue(
		qn: Query,
		qs: QuerySpec,
		path: string,
		op: string,
		value: C8PrimitiveValue,
		index?: number | string,
	): void {
		switch (op) {
			case xml.OP_CONTAINS:
			case xml.OP_EQUALS:
				if ((value === "") || isNullOrUndefined(value) || (value === xml.VALUE_BLANK) || (value === xml.VALUE_NONBLANK)) {
					// use special operators for blank/non-blank values
					op = this.getBlankOperator(value === xml.VALUE_NONBLANK);
					value = undefined;
				} else {
					op = xml.OP_EQUALS;
				}
				break;

			case xml.OP_NOT_EQUALS:
				if ((value === "") || isNullOrUndefined(value)) {
					op = this.getBlankOperator(true);
					value = undefined;
					break;
				}
				// fall through

			case xml.OP_NOT_CONTAINS:
				if (isNullOrUndefined(value)) {
					value = "";
				}
				if (qs.path || (this.ra && this.ra.isOptional)) {
					// "not equals" is not the opposite of "equals" - if the attribute is missing
					// or the path does not exist, then "not equals" will skip it. So it is better to use NOT.
					qn = qn.Not();
					op = getReverseOpToNegate(op) || xml.OP_EQUALS;
				} else {
					// simple not equals when no path and non-optional attribute
					op = xml.OP_NOT_EQUALS;
				}
				break;

			case xml.OP_NOT_MATCHES:
				if ((value === "") || isNullOrUndefined(value)) {
					op = this.getBlankOperator(true);
					value = undefined;
					break;
				}
				qn = qn.Not();
				op = getReverseOpToNegate(op);
				break;

			case xml.OP_MATCHES:
			case xml.OP_RANGE:
				if ((value === "") || isNullOrUndefined(value)) {
					op = this.getBlankOperator();
					value = undefined;
					break;
				}
				break;
		}

		// add condition to query node
		if (qs.id) {
			const bNumber = xml.attributeTypeIsNumeric(qs.type) || (qs.type == xml.ATTR_TIME);
			if (bNumber && isNullOrUndefined(value)) {
				value = "0";
			}

			qn.Attr2(qs.id, op, {
				Index: index,
				Path: path || null,
				SValue: bNumber ? null : value,
				DValue: bNumber ? value : null,
			});
		} else if (qs.prop) {
			qn.Node(qs.prop, op, value, path);
		}
	}

	/**
	 * Add sort condition to query
	 */
	applySort(qn: Query, descending?: boolean): void {
		const qs = this._getSortSpec();
		if (qs.id) {
			qn.SortByAttr(qs.id, qs.path, qs.index, descending);
		} else {
			qn.SortByNode(qs.prop, qs.path, descending);
		}
	}

	/**
	 * Return true if filtering is supported - if bValues specified, indicate if filtering
	 * supports filtering by value rather than just blank vs. non-blank
	 */
	canFilter(bValues?: boolean): boolean {
		const { isMatrix, isMatrixGroup, querySpec } = this;

		return querySpec &&
			!querySpec.notFilterable &&
			!isMatrixGroup &&
			(!isMatrix || querySpec.index) &&
			(!bValues || !querySpec.blanksOnly);
	}

	/**
	 * Return true if spec supports given operator
	 */
	canFilterByOperator(op: string): boolean {
		return !!this.querySpec?.ops?.includes(op);
	}

	/**
	 * Return true if spec supports grouping
	*/
	canGroup(): boolean {
		// filter out if cannot be queried
		const qs = this.querySpec;
		if (!qs || qs.prop) {
			return false;
		}

		// filter out if in matrix
		if (this.key?.matrix) {
			return false;
		}

		// the attribute must be of type enum, ref or boolean
		switch (this.type) {
			case xml.ATTR_BOOLEAN:
			case xml.ATTR_ENUM:
			case xml.ATTR_REF:
				break;

			case xml.ATTR_STRING:
				// allow Node Name attributes if along a path whose last step is __Parent__; in most such cases, rather than
				// adding the __Parent__ "ref" attribute to the view (which would allow grouping), we add the Node Name attribute
				// of the __Parent__ (which does not allow grouping).
				if ((qs.id == xml.ATTR_NAME) && RE_PARENT_PATH.test(qs.path)) {
					break;
				} else if (this.format === types.FORMAT_TYPE) {
					break;
				}

				return false;

			default:
				return false;
		}

		// path must not include 0..* steps
		const ep = this.getExtendedPath();
		if (ep && !ep.isSingle) {
			return false;
		}

		// filter out if attribute is not available in the view
		return this.isAvailable;
	}

	/**
	 * Return true if spec can be reported on, which includes numeric attributes along a stored path
	 */
	canReport(): boolean {
		const { querySpec } = this;

		if (querySpec?.id && xml.attributeTypeIsNumeric(querySpec.type) && !xml.pathHasParentStep(querySpec.path)) {
			return true;
		}

		return false;
	}

	/**
	 * Only makes sense to sort when full filtering is allowed, and spec represents only one value (which is not the case for matrices)
	 */
	canSort(): boolean {
		const { key } = this;
		const qs = this._getSortSpec();
		return qs && !qs.blanksOnly && !qs.notSortable && key && !key.matrix;
	}

	/**
	 * Compare given query values and return true if they are equal
	 */
	compareQueryValues(value1: C8PrimitiveValue[], value2: C8PrimitiveValue[]): boolean {
		const dateRange1 = value1 as C8DateRangeArray;

		if (isDateRangeValue(dateRange1)) {
			return areDateRangeValuesEqual(dateRange1, value2 as C8DateRangeArray);
		}

		if (!this.isDateAttribute()) {
			value1 = [...value1].sort();
			value2 = [...value2].sort();

			// convert boolean values to string values
			if (this.querySpec.type === xml.ATTR_BOOLEAN) {
				value1 = value1.map(stringBoolean2Boolean);
				value2 = value2.map(stringBoolean2Boolean);
			}
		}

		return isEqual(value1, value2);
	}

	/**
	 * If attribute, use U/F operator with blanksOperator (default is "S"); if property, just use null or not-null
	 */
	getBlankOperator(bNot?: boolean): string {
		const { querySpec } = this;

		if (querySpec?.omitBlanks) {
			return null;
		}

		// if blank value is defined - use NE/EQ to compare against it
		if (isDefined(this.querySpec?.ren?.blankValue)) {
			return bNot ? "NE" : "EQ";
		}

		if (querySpec?.id) {
			return (bNot ? xml.OP_NON_BLANKS : xml.OP_BLANKS) + (querySpec.blanksOperator || xml.OP_BLANK_DEFAULT);
		}

		return bNot ? xml.OP_NOT_NULL : xml.OP_NULL;
	}

	/**
	 * Return width to use for Spec in table views
	 */
	getColumnWidth(): number {
		const { format, heading, key, type } = this;
		let size: number | string;

		if (heading) {
			if (heading.columnWidth) {
				// if column width specified on Heading, then that overrides everything
				size = heading.columnWidth;
			} else if (type) {
				// if spec has a type, then lookup first by column id, then format, finally by type - anything else defaults to Small
				const baseType = xml.attributeTypeGetBase(type) || type;
				const attributeWidth = types.getColumnWidthByAttributeId(heading.m_id);

				if (attributeWidth) {
					size = attributeWidth;
				} else if ((type != baseType) && (this.index == null)) {
					// if attribute is container type (map, set, list, vector), and not showing a single entry (no index), then default to Large
					// the index won't be null if the container is a vector
					size = LARGE;
				} else {
					size = types.getColumnWidthByFormatAndType(baseType, format);

					if (!size) {
						// Name column of primary node in view defaults to Medium rather than Small
						size = key.isNameWithoutPath ? MEDIUM : SMALL;
					}
				}
			} else {
				// if type is unknown, then default to Medium
				size = MEDIUM;
			}
		} else {
			// actions column (or empty?)
			size = SMALL;
		}

		// Heading ColumnWidth can be Small/Medium/Large, or a number, so just return size if not found in map
		return (typeof size === "string") ? COLUMN_WIDTHS[size] : size;
	}

	/**
	 * Return key used to store column width in the view
	 */
	getColumnWidthKey(): string {
		let { key } = this;

		if (!key) {
			return "";
		}

		// if spec is part of a column matrix and has an index, assumption is that key will apply to all indices
		if (this.columnUrl && (key.index != null)) {
			key = key.copy({ index: null });
		}

		return key.typedString;
	}

	/**
	 * Return spec name include group name if forced or the spec has a path
	 */
	getExtendedName(bForceGroupName?: boolean): string {
		const ep = this.getExtendedPath();
		let { name } = this;

		if (ep && (ep.depth || bForceGroupName)) {
			name += " (" + locale.type(ep.getEndType()) + ")";
		}

		return name;
	}

	/**
	 * Return extended path for Spec
	 */
	getExtendedPath(): ExtendedPath | undefined {
		return this.key?.extendedPath;
	}

	/**
	 * Return params for Spec header that apply to whole DOM for header (CSS, title, colspan)
	 */
	getHeaderCellParams(params?: SpecHTMLElementParams): SpecHTMLElementParams {
		return pick(params || this.headerParams, HEADER_CELL_PARAMS);
	}

	/**
	 * Return params for Spec header that specify its content (html or text)
	 */
	getHeaderContentParams(params?: SpecHTMLElementParams): SpecHTMLElementParams {
		return pick(params || this.headerParams, HEADER_CONTENT_PARAMS);
	}

	/**
	 * @deprecated
	 * Return string representation of the key
	 */
	getKey(): string | undefined {
		return this.uniqueId;
	}

	/**
	 * Return last sub-heading, if any
	 */
	getLastSub(): Spec | undefined {
		const { subs } = this;
		return subs ? last(subs) : undefined;
	}

	/** convert operator to the correct resource based on the attribute type and return it */
	getLocalizationResBasedOnTypes(operator: string): string {
		const { type } = this.querySpec;
		if (type === xml.ATTR_TIME) {
			return `OP_${operator}_TIME`;
		} else if (xml.attributeTypeIsNumeric(type)) {
			return `OP_${operator}_NUMERIC`;
		} else if (xml.attributeTypeIsContainer(type) && (operator === xml.OP_CONTAINS || operator === xml.OP_NOT_CONTAINS)) {
			return `OP_${operator}_CONTAINER`;
		} else {
			return `OP_${operator}`;
		}
	}

	/**
	 * Return memory store holding available operators
	 */
	getOperatorStore(restoreTextComparisonOperators?: boolean): Memory<OperatorStoreItem> | undefined {
		if (this.querySpec) {
			let blankOp: string;

			const restoreRemovedOperators = restoreTextComparisonOperators && (this.type === VALUE_TYPE.REF || this.type === VALUE_TYPE.STRING);
			const operators = restoreRemovedOperators ? this.querySpec.ops.concat([xml.OP_GREATER_THAN, xml.OP_GREATER_EQUAL, xml.OP_LESS_THAN, xml.OP_LESS_EQUAL]) : this.querySpec.ops;

			const data: OperatorStoreItem[] = operators.map((op) => ({
				id: op,
				name: locale.resource(this.getLocalizationResBasedOnTypes(op)),
				noValue: false,
			}));

			if (blankOp = this.getBlankOperator()) {
				data.push({
					id: blankOp,
					name: locale.resource("OP_BLANK"),
					noValue: true,
				});
			}

			if (blankOp = this.getBlankOperator(true)) {
				data.push({
					id: blankOp,
					name: locale.resource("OP_NOT_BLANK"),
					noValue: true,
				});
			}

			return new Memory({ data });
		}
	}

	/**
	 * Return source class of Spec. In a hierarchical view with multiple levels, it is possible that there are Specs
	 * originating from different classes.
	 */
	getSourceClass(): ReflectionClass | undefined {
		return this.getExtendedPath()?.startClass;
	}

	/**
	 * Return index of spec in Specs
	 */
	getSpecsId(): number {
		return Number(this.sid) || 0;
	}

	/**
	 * Return extended query path used to reach values of Spec
	 */
	getExtendedQueryPath(): ExtendedPath | undefined {
		return this.key.extendedPathThroughAttribute;
	}

	/**
	 * Return standard path used to reach values of Spec
	 */
	getStandardQueryPath(): string {
		return convertToStandardPath(this.getQueryPath());
	}

	/**
	 * Return standard path using for querying spec
	 */
	getQueryPath(ignoreIndex?: boolean): string {
		const { heading, index, querySpec, type } = this;

		if (querySpec) {
			return querySpec.path;
		}

		const ep = this.getExtendedQueryPath();
		if (ep) {
			return ep.getQueryPath(ignoreIndex);
		}

		if (!heading) {
			return "";
		}

		const id = heading.m_id;
		const pathIndex = (index == null) ? heading.m_index : index;
		let path = heading.m_path;

		const bRef = type && isRefValueType(type);
		if (bRef) {
			// case when attribute is of type ref, but with no target class (i.e. target could be any node)
			path = pathCreate(path, id);
		}

		if (!ignoreIndex && isDefined(pathIndex)) {
			path = pathCreate(path, PATH_OP_INDEX, pathIndex.toString());
		}

		// if heading is in matrix, then prepend path to matrix
		// TODO: use parentSpec rather than heading.m_parent?
		if (heading.isMatrixSubHeading()) {
			path = pathCreate(heading.m_parent.matrixGroupPath, path);
		}

		return path;
	}

	/** get reflection class owning this attribute */
	getQuerySpecClass(): ReflectionClass {
		return this.rcDest || this.rc;
	}

	/**
	 * Return text label for spec
	 */
	getText(): string {
		const { headerParams } = this;
		return headerParams.multiLineText || headerParams.innerText || "";
	}

	/**
	 * Return tooltip for spec
	 */
	getTooltip(): string | undefined {
		return this.heading?.getTooltip();
	}

	/**
	 * Return true if the spec has subheaders to render
	 */
	hasSubheadersToRender(): boolean {
		const { subs } = this;
		const n = subs?.length || 0;

		if (n > 1) {
			return true;
		} else if (n === 1) {
			const mp = this.heading.getMatrixProcessor();

			// if no matrix processor, then assume that any subs should be rendered
			if (!mp || mp.shouldRenderLoneSubheader(subs[0])) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Return true if the attribute comes from the same Domain
	 */
	get inSameDomain(): boolean {
		return !this.querySpec.path || !!this.getExtendedPath()?.inSameDomain;
	}

	/**
	 * Return true if the spec is an actions column
	 */
	isActionsColumn(): boolean {
		return !this.heading;
	}

	/**
	 * Return true if attribute is not disabled and not hidden in model (unless explicitly defined in the view)
	 */
	get isAvailable(): boolean {
		// if not an attribute, then no further tests to do
		const { heading, ra, rcDest } = this;
		const { bForceVisible } = heading;

		if (!ra) {
			return true;
		}

		// do not allow disabled attributes
		if (!ra.isEnabled) {
			return false;
		}

		if (rcDest) {
			// relations to secondary class should be omitted
			if (ra.isSecondary) {
				return false;
			}

			// omit relations from one domain class to the secondary class of another
			if (!rcDest.isPrimaryClass && !rcDest.isAbstractClassOfDomainClasses) {
				return false;
			}

			const ep = this.getExtendedPath();
			const endRelation = ep?.endRelation;
			if (endRelation) {
				// relations that reverse on themselves should be omitted
				if (endRelation == ra.bidirectionalRelation) {
					return false;
				}

				// the above test can fail if one of classes in path is abstract, so test specifically for __Parent__ relation
				if (endRelation.isSecondary && (ra.getId() == xml.ATTR_PARENT)) {
					return false;
				}
			}
		} else if (ra.isRelation) {
			// if relation does not lead to a class, then omit; this can be the case for __Parent__ relation of global items
			return bForceVisible;
		}

		// do not allow hidden attributes unless explicitly defined in view
		return !ra.isHidden || bForceVisible;
	}

	/**
	 * Return true if attribute is a date
	 */
	isDateAttribute(): boolean {
		return types.supportsDateRange(this.type, this.format);
	}

	/**
	 * Return true if spec has sub-specs
	 */
	get isGroup(): boolean {
		return !!this.subs?.length;
	}

	/**
	 * Return true if spec represents an image attribute
	 */
	isImage(): boolean {
		return this.format === types.FORMAT_IMAGE;
	}

	/**
	 * Return true if attribute is a localized string, or a ref(container) that points to a localized item
	 */
	isLocalizedAttribute(): boolean {
		return (this.querySpec && (this.querySpec.id == xml.FILTER_LOCALIZED_NAME)) || (this.format && (this.format.indexOf(types.FORMAT_LOCALIZED) == 0));
	}

	/**
	 * Return true if spec is part of a matrix
	 */
	get isMatrix(): boolean {
		return !!this.heading?.matrix;
	}

	/**
	 * Return true if spec is, or is in, a matrix group
	 */
	get isMatrixGroup(): boolean {
		return !!this.heading?.isMatrixGroup;
	}

	/**
	 * Return true if spec should be center aligned in the table
	 */
	isCenterAligned(): boolean {
		return (this.type === xml.ATTR_BOOLEAN) || types.isIconEnum(this.format) || this.isImage();
	}

	/**
	 * Return true if spec should be right aligned in the table
	 */
	isRightAligned(): boolean {
		const { heading, type } = this;

		// Actions column
		if (!heading) {
			return true;
		}

		// Numeric attribute/property
		if (xml.attributeTypeIsNumeric(type) || (type === xml.ATTR_TIME)) {
			return true;
		}

		// Expressions are usually numeric
		if (!type && heading.m_value) {
			return true;
		}

		return false;
	}

	/**
	 * Return true if key matches unique id for Spec
	 */
	matchesKey = (key: C8AttributeKey | Key): boolean => {
		key = Key.toKey(key);
		return (key.typedString === this.key.typedString) || (key.untypedString === this.key.untypedString);
	};

	/**
	 * Set parameter on spec and optionally its sub-specs
	 */
	setParam(param: string, value: any, applyToSubs?: boolean, onlyIfMissing?: boolean): void {
		if (onlyIfMissing && (this[param] != null)) {
			return;
		}

		this[param] = value;

		if (applyToSubs) {
			this.subs?.forEach((sub) => {
				if (!onlyIfMissing || (sub[param] == null)) {
					sub[param] = value;
				}
			});
		}
	}

	/**
	 * Set query spec and update query key
	 */
	setQuerySpec(qs?: QuerySpec): void {
		if (qs && !qs.id && !qs.prop) {
			qs = null;
		}

		this.querySpec = qs;

		if (qs) {
			// make sure query keys do not overlap with Heading Name property is used with same Id as another Heading
			const id = this.heading?.m_nameOrig || qs.id;
			const path = this.getStandardQueryPath();
			this.queryKey = xml.queryKey(id, qs.index, path, qs.prop);
		} else {
			this.queryKey = null;
		}
	}

	/**
	 * Return string representation of Spec
	 */
	toString(): string {
		return this.uniqueId;
	}

	/**
	 * Return QuerySpec to be used for sorting
	 */
	_getSortSpec(): QuerySpec {
		return this.sortQuerySpec || this.querySpec;
	}
};
